import { Box, MenuDivider, MenuItemProps, Tooltip } from '@chakra-ui/react';
import { ReactComponent as NewTabIcon } from 'assets/icons/new-tab.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/toolbar/check.svg';
import { ReactNode, forwardRef } from 'react';
import { isDevelopment, isPreview } from 'utils/environment';

import { Menu } from './Menu';
import { MenuType } from './Menu.types';
import { MenuItem } from './MenuItem';

export interface ToolbarMenuProps extends MenuItemProps {
  children: ReactNode;
  name: string;
  menu: MenuType;
  isRoot?: boolean;
  zIndex?: string;
  isDisabled?: boolean; // Disabled if its view-only
}

export const ToolbarMenu = forwardRef<HTMLElement, ToolbarMenuProps>((inProps, ref) => {
  const { children, name, menu, isRoot = true, zIndex = '', isDisabled } = inProps;
  return (
    <Menu
      buttonValue={children}
      isRoot={isRoot}
      ref={ref}
      key={name}
      menuButtonProps={{
        'aria-label': name,
        minW: '4.25rem',
        _active: {
          bg: 'hover.blue',
          color: 'blue.700',
        },
        disabled: isDisabled,
        display: isRoot ? 'inline-flex' : 'flex',
      }}
      zIndex={zIndex}
      placement={isRoot ? 'bottom-start' : 'right-start'}
    >
      {Object.entries(menu).map(
        ([key, { checked, data, Icon, label, onClick, rightIcon, tooltip, Badge, closeOnSelect = true }]) => {
          if (data != null && label != null) {
            return (
              <MenuItem as={ToolbarMenu} name={label} menu={data} isRoot={false} key={label}>
                {Icon && <Icon width='20' />}
                {label}
              </MenuItem>
            );
          } else if (label != null) {
            return !!tooltip ? (
              <Tooltip key={label} hasArrow placement={'right'} label={tooltip} minWidth={'max-content'}>
                <MenuItem onClick={onClick} key={label} data-testid={key} aria-label={label}>
                  {Icon && <Icon width='20' />}
                  {label}
                  <Box flexGrow={1} />
                  {checked && <CheckIcon />}
                  {rightIcon && <NewTabIcon />}
                  {Badge ? Badge : null}
                </MenuItem>
              </Tooltip>
            ) : (
              <MenuItem
                onClick={onClick}
                key={label}
                data-testid={key}
                aria-label={label}
                closeOnSelect={closeOnSelect}
              >
                {Icon && <Icon width='20' />}
                {label}
                <Box flexGrow={1} />
                {checked && <CheckIcon />}
                {rightIcon && <NewTabIcon />}
                {Badge ? Badge : null}
              </MenuItem>
            );
          } else {
            return <MenuDivider key={key} />;
          }
        },
      )}
    </Menu>
  );
});

if (isDevelopment || isPreview) {
  ToolbarMenu.displayName = 'ToolbarMenu';
}
